<script>
import { Overlay } from '../../'
import { EventBus } from '../../EventBus'

export default {
    name: 'LSR',
    mixins: [Overlay],
    data() {
        return {
            timeframeData: {}
        }
    },
    computed: {
        sett() {
            return this.$props.settings || {}
        },
        long_color() {
            return this.sett.longColor || 'rgba(38, 166, 154, 0.8)'
        },
        short_color() {
            return this.sett.shortColor || 'rgba(239, 83, 80, 0.8)'
        },
        neutral_color() {
            return this.sett.neutralColor || 'rgba(150, 150, 150, 0.6)'
        },
        text_color() {
            return this.sett.textColor || '#ffffff'
        },
        bar_height() {
            return this.sett.barHeight || 20
        },
        show_ratio() {
            return this.sett.showRatio !== false
        },
        show_per_tf() {
            return this.sett.showPerTf !== false
        },
        label_font() {
            return this.sett.font || ('11px ' + (this.$props.font || '').split('px').pop())
        },
        is_dark() {
            const back = this.$props.colors ? this.$props.colors.back : ''
            return back && back !== '#fff' && back !== '#ffffff' && back !== 'white'
        }
    },
    methods: {
        meta_info() {
            return {
                author: 'TradingCandle',
                version: '2.0.0',
                desc: 'Long/Short Ratio - Draws LSR bar and per-timeframe direction indicators on chart',
                preset: {
                    name: 'LSR',
                    side: 'onchart',
                    settings: {
                        longColor: 'rgba(38, 166, 154, 0.8)',
                        shortColor: 'rgba(239, 83, 80, 0.8)',
                        barHeight: 20,
                        showRatio: true,
                        showPerTf: true,
                        'z-index': 10
                    }
                }
            }
        },
        init() {
            EventBus.$on('timeframes-updated', this.onTimeframesUpdated)
            // Request current data immediately
            this.$nextTick(() => {
                EventBus.$emit('request-timeframes')
            })
        },
        destroy() {
            EventBus.$off('timeframes-updated', this.onTimeframesUpdated)
        },
        onTimeframesUpdated(data) {
            if (data && typeof data === 'object') {
                this.timeframeData = data
            }
        },
        draw(ctx) {
            if (!this.show_ratio) return
            const layout = this.$props.layout
            if (!layout || !layout.width) return

            const lsr = this.computeLSR()
            if (!lsr || lsr.total === 0) return

            ctx.save()

            // Draw current Laser Target as dotted line on chart
            const laserTarget = this.getCurrentLaserTarget()
            if (laserTarget && laserTarget.price > 0) {
                const y = Math.round(layout.$2screen(laserTarget.price))
                if (y > 0 && y < layout.height) {
                    const color = laserTarget.direction === 'BUY' ? this.long_color : this.short_color

                    // Dotted line from last candle to right edge
                    let startX = 0
                    if (layout.full_candles && layout.full_candles.length > 0) {
                        startX = layout.full_candles[layout.full_candles.length - 1].x
                    }
                    ctx.strokeStyle = color
                    ctx.lineWidth = 1
                    ctx.setLineDash([2, 2])
                    ctx.beginPath()
                    ctx.moveTo(startX, y)
                    ctx.lineTo(layout.width, y)
                    ctx.stroke()
                    ctx.setLineDash([])

                    // Price label on right edge
                    const label = `LSR ${this.formatNum(laserTarget.price)}`
                    ctx.font = `10px -apple-system, BlinkMacSystemFont, sans-serif`
                    const textWidth = ctx.measureText(label).width
                    const labelX = layout.width - textWidth - 10
                    const labelH = 13
                    const labelW = textWidth + 8

                    ctx.fillStyle = color
                    ctx.globalAlpha = 0.85
                    ctx.fillRect(labelX - 4, y - labelH / 2, labelW, labelH)
                    ctx.globalAlpha = 1
                    ctx.fillStyle = '#ffffff'
                    ctx.textAlign = 'left'
                    ctx.textBaseline = 'middle'
                    ctx.fillText(label, labelX, y)
                }
            }

            // Draw the main LSR bar at the bottom of the chart area
            const barWidth = Math.min(layout.width * 0.4, 260)
            const barX = layout.width - barWidth - 12
            const barY = layout.height - this.bar_height - 8
            const barH = this.bar_height
            const radius = barH / 2

            const longWidth = Math.round(barWidth * lsr.longRatio)
            const shortWidth = barWidth - longWidth

            // Long portion (left, rounded left corners)
            ctx.beginPath()
            ctx.moveTo(barX + radius, barY)
            ctx.lineTo(barX + longWidth, barY)
            ctx.lineTo(barX + longWidth, barY + barH)
            ctx.lineTo(barX + radius, barY + barH)
            ctx.quadraticCurveTo(barX, barY + barH, barX, barY + barH - radius)
            ctx.lineTo(barX, barY + radius)
            ctx.quadraticCurveTo(barX, barY, barX + radius, barY)
            ctx.closePath()
            ctx.fillStyle = this.long_color
            ctx.fill()

            // Short portion (right, rounded right corners)
            ctx.beginPath()
            ctx.moveTo(barX + longWidth, barY)
            ctx.lineTo(barX + barWidth - radius, barY)
            ctx.quadraticCurveTo(barX + barWidth, barY, barX + barWidth, barY + radius)
            ctx.lineTo(barX + barWidth, barY + barH - radius)
            ctx.quadraticCurveTo(barX + barWidth, barY + barH, barX + barWidth - radius, barY + barH)
            ctx.lineTo(barX + longWidth, barY + barH)
            ctx.closePath()
            ctx.fillStyle = this.short_color
            ctx.fill()

            // Text labels on the bar
            ctx.font = `bold ${this.label_font}`
            ctx.textBaseline = 'middle'
            ctx.fillStyle = this.text_color

            // Long label
            if (longWidth > 35) {
                ctx.textAlign = 'left'
                ctx.fillText(`L:${lsr.longCount} (${Math.round(lsr.longRatio * 100)}%)`, barX + 8, barY + barH / 2)
            }
            // Short label
            if (shortWidth > 35) {
                ctx.textAlign = 'right'
                ctx.fillText(`S:${lsr.shortCount} (${Math.round((1 - lsr.longRatio) * 100)}%)`, barX + barWidth - 8, barY + barH / 2)
            }

            // Draw per-timeframe direction dots/markers above the bar
            if (this.show_per_tf && lsr.timeframes.length > 0) {
                const dotRadius = 4
                const dotSpacing = 22
                const dotY = barY - 14
                const startX = barX

                ctx.font = '9px -apple-system, BlinkMacSystemFont, sans-serif'
                ctx.textAlign = 'center'
                ctx.textBaseline = 'bottom'

                lsr.timeframes.forEach((tf, i) => {
                    const x = startX + i * dotSpacing + dotSpacing / 2
                    if (x > barX + barWidth) return

                    // Dot
                    ctx.beginPath()
                    ctx.arc(x, dotY, dotRadius, 0, Math.PI * 2)
                    ctx.fillStyle = tf.direction === 'BUY' ? this.long_color : this.short_color
                    ctx.fill()

                    // Arrow inside dot
                    ctx.fillStyle = this.text_color
                    ctx.font = '8px sans-serif'
                    ctx.textBaseline = 'middle'
                    ctx.fillText(tf.direction === 'BUY' ? '↑' : '↓', x, dotY)

                    // TF label below
                    ctx.fillStyle = tf.direction === 'BUY' ? this.long_color : this.short_color
                    ctx.font = '8px -apple-system, BlinkMacSystemFont, sans-serif'
                    ctx.textBaseline = 'top'
                    ctx.fillText(tf.label, x, dotY + dotRadius + 2)
                })
            }

            ctx.restore()
        },
        computeLSR() {
            const tfData = this.sett.timeframeData || this.timeframeData || {}
            const keys = Object.keys(tfData)
            if (keys.length === 0) return null

            let longCount = 0
            let shortCount = 0
            const timeframes = []

            const tfOrder = ['1m', '3m', '4m', '5m', '10m', '15m', '30m', '40m', '1H', '2H', '4H', '400m', '1D']
            const sortedKeys = keys.sort((a, b) => {
                const aIdx = tfOrder.indexOf(a)
                const bIdx = tfOrder.indexOf(b)
                return (aIdx >= 0 ? aIdx : 99) - (bIdx >= 0 ? bIdx : 99)
            })

            for (const tf of sortedKeys) {
                const item = tfData[tf]
                if (!item || !item.entry) continue
                const className = String(item.className || '')
                if (className.includes('p-hidden')) continue

                const entry = item.entry
                let direction = ''

                if (entry.call === 'BUY' || entry.call === 'SELL') {
                    direction = entry.call
                }
                if (!direction && (entry.flashSide === 'BUY' || entry.flashSide === 'SELL')) {
                    direction = entry.flashSide
                }

                if (direction === 'BUY') longCount++
                else if (direction === 'SELL') shortCount++

                if (direction) {
                    timeframes.push({
                        tf,
                        label: item.label || tf,
                        direction
                    })
                }
            }

            const total = longCount + shortCount
            const longRatio = total > 0 ? longCount / total : 0.5

            return { longCount, shortCount, total, longRatio, timeframes }
        },
        use_for() { return ['LSR'] },
        legend() { return [] },
        getCurrentLaserTarget() {
            const tfData = this.sett.timeframeData || this.timeframeData || {}
            // Find the latest (most recent) laser target across all timeframes
            let latestDate = 0
            let result = null

            const keys = Object.keys(tfData)
            for (const tf of keys) {
                const item = tfData[tf]
                if (!item || !item.entry) continue
                const className = String(item.className || '')
                if (className.includes('p-hidden')) continue

                const entry = item.entry
                if (entry.flashSide && entry.flashTarget && Number(entry.flashTarget) > 0) {
                    const trendDate = Number(entry.latestFlashTrend) || 0
                    if (trendDate >= latestDate) {
                        latestDate = trendDate
                        result = {
                            price: Number(entry.flashTarget),
                            direction: entry.flashSide,
                            tf: item.label || tf
                        }
                    }
                }
            }
            return result
        },
        formatNum(num) {
            if (!num || isNaN(num)) return '-'
            const n = Number(num)
            if (n >= 10000) return n.toFixed(0)
            if (n >= 100) return n.toFixed(1)
            return n.toFixed(2)
        }
    }
}
</script>
